/**
 * "Lean & Clean" onboarding tour (Phase 5B; extended in UX-Audit Phase 3).
 *
 * Eight short steps introduce the biggest changes from the refactor
 * — the slim sidebar, the "Mehr…" accordion, the command palette,
 * the 11-tab patient detail with Portal container, the Beratung
 * timeline, the fixed Heute-Hero on top of the dashboard, and the
 * two existing dashboard quick-action widgets — so a clinician
 * returning to D:E:F:Y after a long weekend doesn't need a
 * support ticket to find a familiar feature.
 *
 * This module is pure data + pure helpers. No React, no driver.js,
 * no localStorage. The consumer
 * (`src/components/onboarding/LeanCleanTourProvider.tsx`) decides
 * when to actually fire the tour — per step, we just declare which
 * DOM element to highlight and what to say. Tests pin every
 * filtering rule below so the tour is safe to keep in the bundle
 * even while the feature matrix evolves.
 *
 * Why each step is dataful rather than JSX:
 *   - The step list is gated by feature flags + current route; we
 *     want to unit-test which steps appear for a given context
 *     without rendering React.
 *   - driver.js accepts exactly this shape
 *     (`{ element, popover: { title, description } }`), so the
 *     runtime conversion is one-to-one.
 */

import type { Feature } from '@/hooks/useFeatures.constants';

// ── Step shape ──────────────────────────────────────────────────────────

export interface TourStep {
  /** Stable id for testing + re-ordering. */
  id: string;
  /** CSS selector of the target element, or `null` for a centre popover. */
  element: string | null;
  /** Popover title shown in bold at the top. */
  title: string;
  /** Body text — HTML is not permitted (cmdk-style plain text). */
  description: string;
  /** Optional side hint shown above the title (e.g. keyboard shortcut). */
  hint?: string;
  /** When present, step is only shown while the user is on a matching path. */
  requiresPath?: RegExp;
  /** When present, step is only shown when ALL features are enabled. */
  requiresAllFeatures?: Feature[];
}

// ── Context passed to `selectTourSteps` ────────────────────────────────

export interface TourContext {
  /**
   * Current pathname from `useLocation().pathname`. Used for steps
   * that only make sense when a given screen is visible — for
   * example, the patient-tab step requires `/patients/:id`.
   */
  pathname: string;
  /**
   * Feature-flag checker from `useFeatures()`. Steps pointing at a
   * feature-gated UI (e.g. the quick-action widgets) are filtered
   * out when the feature is off.
   */
  hasFeature: (feature: Feature) => boolean;
}

// ── The canonical tour ─────────────────────────────────────────────────

/**
 * Tour-Version. Wird beim ersten Anzeigen abgeschlossen in `localStorage`
 * geschrieben und beim Auto-Start gegen `LEAN_CLEAN_TOUR_VERSION` geprüft.
 *
 * Version 1 → 2 (UX-Audit Phase 3, 2026-05-13): Heute-Hero auf dem
 * Dashboard kam neu dazu und braucht einen eigenen Tour-Step. Durch das
 * Bumpen wird die Tour bei jedem bestehenden User ein zweites Mal
 * angezeigt — diesmal mit dem neuen Step.
 */
export const LEAN_CLEAN_TOUR_VERSION = 2;

/**
 * The full tour before any gating. `selectTourSteps` does the final
 * filtering based on the current route + feature flags.
 *
 * Ordering matters: each step builds on the previous one. Start
 * broad (welcome), zoom into the sidebar, then into CMD+K, then
 * patient-context changes, then the dashboard quick-actions.
 */
export const LEAN_CLEAN_TOUR_STEPS: readonly TourStep[] = [
  {
    id: 'welcome',
    element: null, // centre popover — nothing to highlight
    title: 'Willkommen bei D:E:F:Y',
    description:
      'Wir haben das Interface aufgeräumt: weniger Klicks, klarere Wege. Diese kurze Tour zeigt Ihnen, wo jetzt was liegt — jederzeit abbrechbar.',
  },
  {
    id: 'sidebar-primary',
    element: '[data-tour="sidebar-primary"]',
    title: 'Schlanke Hauptnavigation',
    description:
      'Die Sidebar zeigt jetzt die 6 wichtigsten Einträge: Dashboard, Patienten, Kalender, Auswertungen, Ernährungspläne, Nachrichten.',
  },
  {
    id: 'sidebar-more',
    element: '[data-tour="sidebar-more"]',
    title: 'Alles andere unter „Mehr…"',
    description:
      'Sitzungsaufnahme, Rechnungen, Telemedizin und weitere Module sind weiterhin da — einmal aufklappen, und der Admin kann über „Workspace anpassen" in den Einstellungen einzelne Einträge auf die Hauptnavigation heben.',
  },
  {
    id: 'command-palette',
    element: '[data-tour="command-palette-hint"]',
    hint: 'Tipp: ⌘K / Strg+K',
    title: 'Alles finden in einer Sekunde',
    description:
      'Mit ⌘K (oder Strg+K) öffnen Sie die Suche. Tippen Sie den Namen eines Patienten, einer Seite oder einer Aktion — auch versteckte Tabs wie Outcome-Tracking finden Sie hier.',
  },
  {
    id: 'patient-tabs',
    element: '[data-tour="patient-tabs"]',
    requiresPath: /^\/patients\/[^/?#]+/,
    title: 'Fokussierte Patient-Tabs',
    description:
      'Die Patientenakte ist jetzt schlank gegliedert. Verwandte Funktionen (Messwerte, Beratung, Analyse, Portal-Zugriff/Aktivität, …) stecken als Sub-Tabs in einer Karte — ein Klick, alle Details sichtbar.',
  },
  {
    id: 'beratung-timeline',
    element: '[data-tour="beratung-timeline"]',
    requiresPath: /^\/patients\/[^/?#]+/,
    title: 'Beratung als Timeline',
    description:
      'Termine, Sitzungsnotizen und GNCP-Einträge werden pro Termin gruppiert — eine Karte pro Beratung, alles auf einen Blick, statt drei separater Listen.',
  },
  {
    id: 'today-hero',
    element: '[data-tour="today-hero"]',
    requiresPath: /^\/dashboard$/,
    title: 'Ihr Tagesüberblick',
    description:
      'Neu oben auf dem Dashboard: Nächster Termin, Anzahl Termine heute und Ihre offenen Aufgaben in einer Zeile — fester Anker, lässt sich nicht ausblenden.',
  },
  {
    id: 'dashboard-quick-actions',
    element: '[data-tour="dashboard-quick-actions"]',
    requiresPath: /^\/dashboard$/,
    title: 'Quick-Actions auf dem Dashboard',
    description:
      '„Nächster Termin" und „Letzte Sitzung fortsetzen" sind neu: ein Klick, und Sie landen direkt beim richtigen Patienten — kein Scannen der Schedule mehr.',
  },
];

// ── Gating ──────────────────────────────────────────────────────────────

/**
 * Filter the canonical tour list down to the steps that make sense
 * right now. Steps are dropped when their `requiresPath` or
 * `requiresAllFeatures` does not match the current context. Pure;
 * tests pin every branch.
 */
export function selectTourSteps(
  steps: readonly TourStep[],
  ctx: TourContext,
): TourStep[] {
  return steps.filter((step) => {
    if (step.requiresPath && !step.requiresPath.test(ctx.pathname)) {
      return false;
    }
    if (step.requiresAllFeatures) {
      for (const f of step.requiresAllFeatures) {
        if (!ctx.hasFeature(f)) return false;
      }
    }
    return true;
  });
}

// ── Completion persistence ─────────────────────────────────────────────

const STORAGE_KEY = 'lean-clean-tour-completed-version';

/**
 * The tour shows once per user per version. Bumping
 * `LEAN_CLEAN_TOUR_VERSION` re-runs the tour for everyone — useful
 * the next time we reshape the UI meaningfully.
 */
export function readCompletedTourVersion(): number | null {
  if (typeof window === 'undefined') return null;
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (raw == null) return null;
    const n = Number.parseInt(raw, 10);
    return Number.isFinite(n) ? n : null;
  } catch {
    return null;
  }
}

export function writeCompletedTourVersion(version: number): void {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.setItem(STORAGE_KEY, String(version));
  } catch {
    // Private mode / blocked storage — silently give up. Worst
    // case the user sees the tour once more on next login.
  }
}

export function clearCompletedTourVersion(): void {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.removeItem(STORAGE_KEY);
  } catch {
    // Same reasoning as `writeCompletedTourVersion`.
  }
}

/**
 * Decide whether the tour should auto-start on this render.
 * Returns `true` when the user has never completed this version
 * (or a newer one) of the tour. Pure so the component wiring can
 * be kept small.
 */
export function shouldAutoStartTour(
  completedVersion: number | null,
  currentVersion: number = LEAN_CLEAN_TOUR_VERSION,
): boolean {
  if (completedVersion == null) return true;
  return completedVersion < currentVersion;
}
